import { useEffect } from 'react';

export type ShortcutDef = {
  key: string;           // e.g. '1', 'j', 'Mod+Enter' — Mod is Cmd on macOS, Ctrl elsewhere
  action: () => void;
  label: string;
  group: 'Navigation' | 'Actions';
};

function isInputElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

function parseShortcut(key: string): { mod: boolean; shift: boolean; keyName: string } {
  const parts = key.split('+');
  const keyName = parts[parts.length - 1];
  const mod = parts.includes('Mod');
  const shift = parts.includes('Shift');
  return { mod, shift, keyName };
}

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  // `userAgentData` is Chromium-only and not yet in the DOM lib types.
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  return /mac|iphone|ipad|ipod/i.test(uaData?.platform ?? navigator.platform ?? '');
}

/** Renders a shortcut for display, e.g. 'Mod+Enter' → '⌘+Enter' on macOS, 'Ctrl+Enter' elsewhere. */
export function formatShortcut(key: string, isMac = isMacPlatform()): string {
  return key
    .split('+')
    .map(part => (part === 'Mod' ? (isMac ? '⌘' : 'Ctrl') : part))
    .join('+');
}

export function useKeyboardShortcuts(shortcuts: ShortcutDef[], enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      for (const shortcut of shortcuts) {
        const parsed = parseShortcut(shortcut.key);

        if (parsed.mod) {
          // Modifier shortcuts (Mod+X, Mod+Shift+X) fire even in inputs. Either Cmd or Ctrl
          // counts as Mod, so they work without trusting platform detection.
          if ((e.metaKey || e.ctrlKey) && e.shiftKey === parsed.shift && e.key.toLowerCase() === parsed.keyName.toLowerCase()) {
            e.preventDefault();
            shortcut.action();
            return;
          }
        } else {
          // Single-key shortcuts: skip if in an input or modifier held
          if (isInputElement(document.activeElement)) continue;
          if (e.metaKey || e.ctrlKey || e.altKey) continue;

          if (e.key === parsed.keyName) {
            e.preventDefault();
            shortcut.action();
            return;
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, enabled]);
}
